import { Link } from "react-router-dom";
import { useA11y } from "../a11y/A11yContext";
import { Toggle, Btn, Sheet, SheetActions } from "./Shared";

const TEXT_SIZES = [
  { value: 100, label: "Normal" },
  { value: 125, label: "Large" },
  { value: 150, label: "Larger" },
];

function Option({ title, description, checked, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, padding: "12px 0", borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "#1a1a1a", fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{description}</div>
      </div>
      <Toggle checked={checked} onChange={onChange} label={title} />
    </div>
  );
}

// `statementPath` is a prop rather than a fixed "/accessibility" so an app that puts
// its statement on another route — or has none yet — can still use this panel.
export default function AccessibilityPanel({ onClose, statementPath = "/accessibility" }) {
  const { prefs, set, reset, anyEnabled } = useA11y();

  // A dialog that traps nothing and can't be dismissed with Escape is a trap
  // for exactly the people this panel exists for. Sheet does both, gives focus
  // back to the button that opened it, and is a full-width sheet on a phone
  // (2026-10-20, as data-portal's).
  return (
    <Sheet labelledBy="a11y-title" onClose={onClose} maxWidth={520} zIndex={200}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 4 }}>
          <div style={{ flex: 1 }}>
            <h2 id="a11y-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Accessibility</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
              These settings are kept on this device and apply straight away, signed in or not.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close accessibility settings"
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, lineHeight: 1, color: "#6b7280", minWidth: 44, minHeight: 44, margin: "-10px -10px 0 0" }}
          >×</button>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ padding: "12px 0", borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ fontSize: 14, color: "#1a1a1a", fontWeight: 500 }}>Text size</div>
            <div style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 10px" }}>
              Your browser's own zoom still works on top of this.
            </div>
            <div role="radiogroup" aria-label="Text size" style={{ display: "flex", gap: 8 }}>
              {TEXT_SIZES.map(s => {
                const active = prefs.textScale === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => set("textScale", s.value)}
                    style={{
                      flex: 1, padding: "8px 12px", borderRadius: 8, cursor: "pointer",
                      fontFamily: "inherit", fontSize: 13, fontWeight: active ? 700 : 500,
                      border: active ? "2px solid #007a33" : "1px solid #9ca3af",
                      background: active ? "rgba(0,150,64,0.08)" : "#fff",
                      color: active ? "#00551f" : "#374151",
                    }}
                  >
                    {s.label} {active && <span aria-hidden="true">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <Option
            title="High contrast"
            description="Darker text and accents, heavier outlines around form fields and buttons."
            checked={prefs.highContrast}
            onChange={v => set("highContrast", v)}
          />
          <Option
            title="Colour-blind safe palette"
            description="Replaces green/red with a blue/orange pairing that stays distinguishable. Statuses always carry a symbol as well."
            checked={prefs.colorBlindSafe}
            onChange={v => set("colorBlindSafe", v)}
          />
          <Option
            title="Reduce motion"
            description="Turns off transitions and spinners' animation."
            checked={prefs.reduceMotion}
            onChange={v => set("reduceMotion", v)}
          />
          <Option
            title="Larger click targets"
            description="Gives buttons and links a minimum size of 44 by 44 pixels."
            checked={prefs.largeTargets}
            onChange={v => set("largeTargets", v)}
          />
        </div>

        <p style={{ fontSize: 12, color: "#6b7280", marginTop: 16 }}>
          Some of these are picked up automatically from your operating system's own settings.
          See the <Link to={statementPath} onClick={onClose} style={{ color: "#005587" }}>accessibility statement</Link> for
          what this app does and does not yet support.
        </p>

        <SheetActions style={{ justifyContent: "space-between", marginTop: 16 }}>
          <Btn variant="secondary" onClick={reset} disabled={!anyEnabled}>Reset to system defaults</Btn>
          <Btn onClick={onClose}>Done</Btn>
        </SheetActions>
    </Sheet>
  );
}
